import React from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';

const FilterModal = ({
  isOpen,
  onClose,
  searchRadius,
  setSearchRadius,
  isRadiusFilterEnabled,
  setIsRadiusFilterEnabled,
  distanceUnit,
  setDistanceUnit,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleUnitToggle = () => {
    setDistanceUnit(prev => prev === 'miles' ? 'km' : 'miles');
  };

  const radiusInCurrentUnit = distanceUnit === 'km' ? searchRadius * 1.60934 : searchRadius;
  const maxRadiusInCurrentUnit = distanceUnit === 'km' ? 20000 * 1.60934 : 20000;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl text-white w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-blue-300">{t('filterModal.title', 'Filter Options')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <LucideIcons.X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Unit Toggle */}
          <div className="flex items-center justify-between">
            <label htmlFor="unit-toggle" className="font-semibold">{t('filterModal.distanceUnit', 'Distance Unit')}</label>
            <div className="flex items-center bg-gray-700 rounded-full p-1">
              <button onClick={() => setDistanceUnit('miles')} className={`px-3 py-1 text-sm rounded-full ${distanceUnit === 'miles' ? 'bg-blue-500' : ''}`}>
                {t('filterModal.miles', 'Miles')}
              </button>
              <button onClick={() => setDistanceUnit('km')} className={`px-3 py-1 text-sm rounded-full ${distanceUnit === 'km' ? 'bg-blue-500' : ''}`}>
                {t('filterModal.km', 'Km')}
              </button>
            </div>
          </div>

          {/* Radius Filter Toggle */}
          <div className="flex items-center justify-between">
            <label htmlFor="radius-toggle" className="font-semibold">{t('filterModal.enableRadiusFilter', 'Enable Radius Filter')}</label>
            <button
              id="radius-toggle"
              onClick={() => setIsRadiusFilterEnabled(!isRadiusFilterEnabled)}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 ${
                isRadiusFilterEnabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ${
                  isRadiusFilterEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Search Radius Slider */}
          <div className={`transition-opacity duration-300 ${isRadiusFilterEnabled ? 'opacity-100' : 'opacity-50'}`}>
            <label htmlFor="radius-slider" className="block mb-2 font-semibold">{t('filterModal.searchRadius', 'Search Radius')}: <span className="font-normal">{radiusInCurrentUnit.toFixed(0)} {distanceUnit}</span></label>
            <input
              id="radius-slider"
              type="range"
              min="1"
              max={maxRadiusInCurrentUnit}
              step="1"
              value={radiusInCurrentUnit}
              onChange={(e) => {
                const valueInCurrentUnit = Number(e.target.value);
                const valueInMiles = distanceUnit === 'km' ? valueInCurrentUnit / 1.60934 : valueInCurrentUnit;
                setSearchRadius(valueInMiles);
              }}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              disabled={!isRadiusFilterEnabled}
            />
          </div>
        </div>

        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300"
          >
            {t('filterModal.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
